/**
 * Petits helpers de présentation partagés par le tableau de bord admin (redesign 2026).
 * Une seule palette pour les pastilles de statut (teinte douce + texte foncé de la même famille),
 * des icônes Material Symbols à la place des emoji, et les en-têtes de page.
 */
import { escapeHtml } from '../utils/html.js';

const PILL_TONES = ['success', 'warning', 'danger', 'info', 'accent', 'neutral'];

/**
 * Pastille de statut : `<span class="ad-pill ad-pill--success">Succès</span>`.
 * @param {string} label texte affiché (échappé)
 * @param {string} tone l'un de PILL_TONES (neutral par défaut)
 * @param {{ dot?: boolean, mono?: boolean }} options
 */
export function pill(label, tone = 'neutral', { dot = true, mono = false } = {}) {
    const safeTone = PILL_TONES.includes(tone) ? tone : 'neutral';
    const classes = ['ad-pill', `ad-pill--${safeTone}`];
    if (!dot) classes.push('ad-pill--plain');
    if (mono) classes.push('ad-pill--mono');
    return `<span class="${classes.join(' ')}">${escapeHtml(label ?? '—')}</span>`;
}

const EXECUTION_TONES = { success: 'success', warning: 'warning', error: 'danger' };
const REQUEST_TONES = { pending: 'warning', in_progress: 'info', completed: 'success' };
const SUPPORT_TONES = {
    new: 'warning',
    pending: 'warning',
    acknowledged: 'info',
    in_progress: 'info',
    waiting_user: 'accent',
    waiting_response: 'accent',
    replied: 'accent',
    waiting_support: 'warning',
    resolved: 'success',
    closed: 'neutral',
};
const ERROR_LEVEL_TONES = { error: 'danger', warn: 'warning', warning: 'warning', info: 'info' };
const EMAIL_STATUS_TONES = { success: 'success', sent: 'success', failed: 'danger' };

export function toneFor(kind, value) {
    const maps = {
        execution: EXECUTION_TONES,
        request: REQUEST_TONES,
        support: SUPPORT_TONES,
        errorLevel: ERROR_LEVEL_TONES,
        email: EMAIL_STATUS_TONES,
    };
    if (kind === 'event') {
        const type = String(value || '');
        if (type.includes('error') || type.includes('failed') || type.includes('deleted')) return 'danger';
        if (type.includes('warning')) return 'warning';
        if (type.includes('success') || type.includes('created')) return 'success';
        return 'info';
    }
    return maps[kind]?.[value] || 'neutral';
}

/** Icônes Material Symbols des modèles d'emails (l'API renvoie des emoji). */
const TEMPLATE_ICONS = {
    verification: 'mark_email_read',
    password_reset: 'lock_reset',
    scenario_shared: 'share',
    scenario_updated: 'edit_note',
    support_status_change: 'sync_alt',
    support_reply: 'forum',
    admin_welcome: 'waving_hand',
    admin_announcement: 'campaign',
    admin_notification: 'notifications',
    admin_custom: 'edit_square',
    custom: 'edit_square',
};

export function templateIcon(template) {
    return TEMPLATE_ICONS[template?.id] || 'mail';
}

/** Titre + sous-titre de chaque section du menu. */
export const PAGE_HEADERS = {
    overview: { title: 'Tableau de bord', subtitle: 'Activité de la plateforme sur les 30 derniers jours.' },
    users: { title: 'Utilisateurs', subtitle: 'Comptes, rôles et dernières connexions.' },
    requests: { title: 'Demandes', subtitle: 'Demandes d’applications, de commandes et suggestions.' },
    support: { title: 'Support', subtitle: 'Tickets envoyés par les utilisateurs.' },
    executions: { title: 'Exécutions', subtitle: 'Lancements de scénarios et leur résultat.' },
    events: { title: 'Journal d’activité', subtitle: 'Événements enregistrés sur les scénarios et les demandes.' },
    errors: { title: 'Erreurs', subtitle: 'Erreurs remontées par l’app, l’API et Electron.' },
    emails: { title: 'Historique des emails', subtitle: 'Tous les emails envoyés. Pour en envoyer un, passez par les modèles.' },
    lifecycle: {
        title: 'Parcours automatiques',
        subtitle: 'E-mails et push envoyés selon l’activité des utilisateurs : activation, app mobile, réengagement.',
    },
    templates: { title: 'Modèles d’emails', subtitle: 'Tous les emails de l’application. Choisissez un modèle pour l’envoyer.' },
};

export function renderPageHeader(tab, actionsHtml = '') {
    const header = PAGE_HEADERS[tab] || PAGE_HEADERS.overview;
    return `
        <header class="ad-page-head">
            <div class="ad-page-heading">
                <h1 class="ad-page-title">${escapeHtml(header.title)}</h1>
                <p class="ad-page-sub">${escapeHtml(header.subtitle)}</p>
            </div>
            ${actionsHtml ? `<div class="ad-page-actions">${actionsHtml}</div>` : ''}
        </header>
    `;
}
